/** Progress — design spec §7. A 4px bar and a conic-gradient ring. */

import { cn } from "@/lib/cn";

function clampRatio(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, done / total));
}

/**
 * §7 — 4px track on `--sep`, fill on `--accent`. A finished project fills with
 * `--hint` instead: done work is settled, and the accent belongs to what is
 * still live.
 */
export function ProgressBar({
  done,
  total,
  complete,
  className,
}: {
  done: number;
  total: number;
  complete?: boolean;
  className?: string;
}) {
  const ratio = clampRatio(done, total);
  const isComplete = complete ?? (total > 0 && done >= total);
  return (
    <div
      className={cn("h-1 w-full overflow-hidden rounded-sm bg-sep", className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-label={`Выполнено ${done} из ${total}`}
    >
      <div
        className={cn("h-full rounded-sm", isComplete ? "bg-hint" : "bg-accent")}
        style={{ width: `${ratio * 100}%` }}
      />
    </div>
  );
}

/**
 * §7 — `conic-gradient(--accent 0 X%, --sep X% 100%)` with a `--bg` disc
 * punched out of the middle; the centre carries "3/8" or a percentage.
 */
export function ProgressRing({
  done,
  total,
  size = 64,
  label = "fraction",
  className,
}: {
  done: number;
  total: number;
  size?: number;
  label?: "fraction" | "percent";
  className?: string;
}) {
  const ratio = clampRatio(done, total);
  const percent = Math.round(ratio * 100);
  const isComplete = total > 0 && done >= total;
  const fill = isComplete ? "var(--hint)" : "var(--accent)";

  return (
    <div
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Выполнено ${done} из ${total}`}
    >
      <div
        className="h-full w-full rounded-full"
        style={{ background: `conic-gradient(${fill} 0 ${percent}%, var(--sep) ${percent}% 100%)` }}
      />
      <div
        className="absolute rounded-full bg-bg"
        // A 5px ring reads at every size the spec uses (56–64).
        style={{ inset: 5 }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-cell-title font-medium text-text">
          {label === "percent" ? `${percent}%` : `${done}/${total}`}
        </span>
      </div>
    </div>
  );
}
